import InputSelect from "@Components/Input/InputSelect/InputSelect";
import InputTagSelection from "@Components/Input/InputTagSelection";
import { InputAction } from "@Components/Input/InputText/InputText";
import { BORDER_TYPE } from "@Configs/enum";
import { CommonService } from "@Services/common-service";
import { Add, Checkmark } from "@carbon/icons-react";
import { Checkbox, Empty, Tooltip } from "antd";
import classNames from "classnames";
import React, { RefObject } from "react";
import { Model } from "react-3layer-common";
import { ErrorObserver, Observable, Subscription } from "rxjs";
import "./EnumSelect.scss";
import EmptyCM from "@Components/EmptyCM";

export interface SelectProps<T extends Model> {
  /**Value users select*/
  value?: Model;
  /**List value users select*/
  listValue?: Model[];
  /**Placeholder of the component*/
  placeHolder?: string;
  /**Not allow to handle change value*/
  disabled?: boolean;
  /**Append this component to body*/
  appendToBody?: boolean;
  /**Show symbol * as required field*/
  isRequired?: boolean;
  /**Handle the change value of the field*/
  onChange?: (id: number, T?: T) => void;
  /**Handle the change list value of the component */
  onChangeMultiple?: (selectedList?: T[], ids?: []) => void;
  /**Provide a function to render a specific property as name*/
  render?: (t: T) => string;
  /**Control the style type of component: MATERIAL, BORDERED, FLOAT_LABEL */
  type?: BORDER_TYPE;
  /**Label for current field*/
  label?: string;
  /**Option show button add new*/
  selectWithAdd?: () => void;
  /**Control the size of the component*/
  isSmall?: boolean;
  /**Prefer option to select*/
  preferOptions?: T[];
  /**Option to select multiple*/
  isMultiple?: boolean;
  /**Api to get list data*/
  getList?: () => Observable<T[]>;
  /**Custom height of dropdown data list*/
  height?: number;
  /** Provide a custom action (onClick) to the component */
  action?: InputAction;
  /** Custom background color for component: "white" || "gray" */
  bgColor?: "white" | "gray";
  /**Use to custom style the component*/
  className?: string;
  /**Use to set time get list to call*/
  isLoadMultipleTimes?: boolean;
}

function defaultRenderObject<T extends Model>(t: T) {
  return t?.name;
}

interface changeAction {
  type: string;
  data: Model;
}

function multipleSelectReducer(
  currentState: Model[],
  action: changeAction
): Model[] {
  switch (action.type) {
    case "UPDATE":
      return [...currentState, action.data];
    case "REMOVE":
      // eslint-disable-next-line no-case-declarations
      const filteredArray = currentState.filter(
        (item) => item.id !== action.data.id
      );
      return [...filteredArray];
    case "REMOVE_ALL":
      return [];
  }
  return;
}

function EnumSelect(props: SelectProps<Model>) {
  const {
    appendToBody = false,
    render = defaultRenderObject,
    disabled = false,
    value,
    placeHolder,
    isRequired,
    onChange,
    type,
    label,
    selectWithAdd,
    isSmall,
    isMultiple,
    listValue,
    onChangeMultiple,
    getList,
    height,
    action,
    bgColor,
    className,
    isLoadMultipleTimes,
  } = props;

  const internalValue = React.useMemo((): Model => {
    return value || null;
  }, [value]);

  const [list, setList] = React.useState<Model[]>([]);

  const [isExpand, setExpand] = React.useState<boolean>(false);

  const [firstLoad, setFirstLoad] = React.useState<boolean>(true);

  const wrapperRef: RefObject<HTMLDivElement> =
    React.useRef<HTMLDivElement>(null);

  const selectListRef: RefObject<HTMLDivElement> =
    React.useRef<HTMLDivElement>(null);

  const [selectedList, dispatch] = React.useReducer(multipleSelectReducer, []);

  const [appendToBodyStyle, setAppendToBodyStyle] = React.useState({});

  React.useEffect(() => {
    const subscription = new Subscription();
    if (isLoadMultipleTimes) {
      subscription.add(getList);
      getList().subscribe({
        next: (res: Model[]) => {
          setList(res);
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        error: (err: ErrorObserver<Error>) => {
          setList([]);
        },
      });
    } else {
      if (firstLoad) {
        subscription.add(getList);
        getList().subscribe({
          next: (res: Model[]) => {
            setList(res);
          },
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          error: (err: ErrorObserver<Error>) => {
            setList([]);
          },
        });
      }
    }
    return function cleanup() {
      subscription.unsubscribe();
    };
  }, [firstLoad, getList, isLoadMultipleTimes]);

  // use this for multiple type
  const internalList = React.useMemo(() => {
    if (list && list.length > 0) {
      list.forEach((current) => {
        const filteredItem =
          listValue &&
          listValue.length > 0 &&
          listValue?.filter((item) => item.id === current.id)[0];
        if (filteredItem) {
          current.isSelected = true;
        } else {
          current.isSelected = false;
        }
      });
      return [...list];
    }
    return [];
  }, [list, listValue]);

  React.useEffect(() => {
    if (firstLoad) {
      if (internalList && internalList?.length > 0) {
        const tempList = [...internalList];
        if (tempList && tempList?.length > 0) {
          tempList.forEach((item) => {
            if (item?.isSelected === true) {
              dispatch({
                type: "UPDATE",
                data: item,
              });
            }
          });
          setFirstLoad(false);
        }
      }
    }
  }, [firstLoad, internalList]);

  const handleToggle = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!disabled) {
        if (!isExpand) {
          setExpand(true);
        } else handleCloseSelect();
      }
    },
    [disabled, isExpand]
  );

  const handleCloseSelect = React.useCallback(() => {
    setExpand(false);
  }, []);

  // use this function for single type
  const handleClickItem = React.useCallback(
    (item: Model) => (event: any) => {
      // perform sort
      const currentIndex = list.findIndex((current) => current.id === item.id);
      list.splice(currentIndex, 1);
      list.unshift(item);
      setList(list);
      onChange(item.id, item);
      handleCloseSelect();
    },
    [handleCloseSelect, list, onChange]
  );

  // use this function for multiple type
  // UX: after choose an item, this item need to be on top of the list
  const handleClickMultiItem = React.useCallback(
    (item: Model) => (event: any) => {
      const filteredItem = listValue?.filter(
        (current) => current.id === item.id
      )[0];

      if (filteredItem) {
        const tmp = [...selectedList];
        const ids = selectedList?.map((item) => item?.id);
        const index = tmp.indexOf(filteredItem);
        const indexIds = tmp.indexOf(filteredItem?.id);
        tmp.splice(index, 1);
        ids.splice(indexIds, 1);
        dispatch({
          type: "REMOVE",
          data: item,
        });
        onChangeMultiple([...tmp], ids as any);
      } else {
        // perform sort
        const currentIndex = list.findIndex(
          (current) => current.id === item.id
        );
        list.splice(currentIndex, 1);
        list.unshift(item);
        setList(list);
        const ids = selectedList?.map((item) => item?.id);
        onChangeMultiple([...selectedList, item], [...ids, item?.id] as any);
        dispatch({
          type: "UPDATE",
          data: item,
        });
      }
    },
    [list, listValue, onChangeMultiple, selectedList]
  );

  const handleClickMultiParentItem = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (event && event.target === event.currentTarget) {
        const currentItem = event.target as HTMLDivElement;
        currentItem.firstElementChild.firstElementChild
          .querySelector("span")
          .click();
      }
    },
    []
  );

  const handleClearItem = React.useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  // use this for type multiple
  const handleClearAll = React.useCallback(() => {
    onChangeMultiple([], []);
    dispatch({
      type: "REMOVE_ALL",
      data: [],
    });
  }, [onChangeMultiple]);

  const handleKeyPress = React.useCallback(
    (event: any) => {
      switch (event.keyCode) {
        case 40:
          // eslint-disable-next-line no-case-declarations
          const firstItem = selectListRef.current
            .firstElementChild as HTMLElement;
          firstItem.focus();
          break;
        case 9:
          handleCloseSelect();
          break;
        default:
          return;
      }
    },
    [handleCloseSelect]
  );

  const handleMove = React.useCallback(
    (item: Model) => (event: any) => {
      switch (event.keyCode) {
        case 13:
          if (!isMultiple) {
            handleClickItem(item)(null);
          } else {
            handleClickMultiItem(item)(null);
          }

          break;
        case 40:
          if (event.target.nextElementSibling !== null) {
            event.target.nextElementSibling.focus();
          }
          event.preventDefault();
          break;
        case 38:
          if (event.target.previousElementSibling !== null) {
            event.target.previousElementSibling.focus();
          }
          event.preventDefault();
          break;
      }
      return;
    },
    [handleClickItem, handleClickMultiItem, isMultiple]
  );

  const handleKeyEnter = React.useCallback(
    (event: any) => {
      if (event.key === "Enter") {
        handleToggle(null);
      }
      return;
    },
    [handleToggle]
  );

  CommonService.useClickOutside(wrapperRef, handleCloseSelect);

  React.useEffect(() => {
    if (isExpand && appendToBody) {
      const currentPosition = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - currentPosition.bottom;
      if (spaceBelow <= 200) {
        setTimeout(() => {
          setAppendToBodyStyle({
            position: "fixed",
            bottom: spaceBelow + wrapperRef.current.clientHeight,
            left: currentPosition.left,
            maxWidth: wrapperRef.current.clientWidth,
          });
        }, 100);
      } else {
        setAppendToBodyStyle({
          position: "fixed",
          top: currentPosition.top + wrapperRef.current.clientHeight,
          left: currentPosition.left,
          maxWidth: wrapperRef.current.clientWidth,
        });
      }
    }
  }, [appendToBody, isExpand]);

  return (
    <>
      <div
        className={classNames("select__container", className, {
          "multiple-select__container": isMultiple,
        })}
        ref={wrapperRef}
      >
        <div className="select__input" onClick={handleToggle}>
          {isMultiple ? (
            <InputTagSelection
              listValue={listValue}
              render={render}
              placeHolder={placeHolder}
              disabled={disabled}
              onClear={handleClearItem}
              type={type}
              label={label}
              isSmall={isSmall}
              isUsingSearch={false}
              onClearMulti={handleClearAll}
              isNotExpand={!isExpand}
              isRequired={isRequired}
              isShowTooltip
              bgColor={bgColor}
              handlePressExpandedIcon={handleCloseSelect}
            />
          ) : (
            <InputSelect
              value={internalValue}
              render={render}
              placeHolder={placeHolder}
              expanded={isExpand}
              disabled={disabled}
              onClear={handleClearItem}
              onKeyDown={handleKeyPress}
              onKeyEnter={handleKeyEnter}
              type={type}
              label={label}
              isSmall={isSmall}
              isEnumerable={true}
              isRequired={isRequired}
              action={action}
              bgColor={bgColor}
              handlePressExpandedIcon={handleCloseSelect}
            />
          )}
        </div>
        {isMultiple
          ? isExpand && (
              <div className="select__list-container">
                {
                  <>
                    <div
                      className="select__list multiple-select__list"
                      ref={selectListRef}
                      style={{ maxHeight: `${height}px` }}
                    >
                      {internalList && internalList.length > 0 ? (
                        internalList.map((item, index) => (
                          <div
                            className={classNames(
                              "select__item p-l--xs p-y--xs p-r--2xs",
                              {
                                "select__item--selected": item.isSelected,
                              }
                            )}
                            key={index}
                            onKeyDown={handleMove(item)}
                            tabIndex={-1}
                            onClick={handleClickMultiParentItem}
                          >
                            <Checkbox
                              checked={item.isSelected}
                              onChange={handleClickMultiItem(item)}
                            >
                              <span className="select__text">
                                <Tooltip
                                  title={render(item)}
                                  placement="topLeft"
                                >
                                  <div className="text-ellipsis">
                                    {render(item)}
                                  </div>
                                </Tooltip>
                              </span>
                            </Checkbox>
                          </div>
                        ))
                      ) : (
                        <EmptyCM />
                      )}
                    </div>
                  </>
                }

                {typeof selectWithAdd !== "undefined" && (
                  <div
                    className={classNames(
                      "select__bottom-button select__add-button p-y--xs"
                    )}
                    onClick={selectWithAdd}
                  >
                    <Add size={16} className="m-l--xs" />
                    <span className="m-l--xs">Add new</span>
                  </div>
                )}
              </div>
            )
          : isExpand && (
              <div className="select__list-container" style={appendToBodyStyle}>
                {
                  <>
                    <div
                      className="select__list"
                      ref={selectListRef}
                      style={{ maxHeight: `${height}px` }}
                    >
                      {list && list?.length > 0 ? (
                        list.map((item, index) => (
                          <div
                            className={classNames(
                              "select__item p-l--xs p-y--xs",
                              {
                                "select__item--selected":
                                  item?.id === internalValue?.id,
                              }
                            )}
                            tabIndex={-1}
                            key={index}
                            onKeyDown={handleMove(item)}
                            onClick={handleClickItem(item)}
                          >
                            <span className="select__text">
                              <Tooltip title={render(item)} placement="topLeft">
                                <div className="text-ellipsis">
                                  {render(item)}
                                </div>
                              </Tooltip>
                            </span>
                            {item?.id === internalValue?.id && (
                              <Checkmark size={16} />
                            )}
                          </div>
                        ))
                      ) : (
                        <EmptyCM />
                      )}
                    </div>
                  </>
                }
                {typeof selectWithAdd !== "undefined" && (
                  <div
                    className={classNames(
                      "select__bottom-button select__add-button p-y--xs"
                    )}
                    onClick={selectWithAdd}
                  >
                    <Add size={16} className="m-l--2xs" />
                    <span>Add new</span>
                  </div>
                )}
              </div>
            )}
      </div>
    </>
  );
}

export default EnumSelect;
